"use client";

import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSession } from "next-auth/react";
import { useDebugRenderLoop } from "~/lib/debug-render-loop";

const GREETINGS = [
  (name: string) =>
    `Welcome, ${name}. Let's turn your office vision into reality.`,
  (name: string) => `Welcome back, ${name}. Let's make your next lease a flex.`,
  (name: string) =>
    `Welcome back, ${name}. Let's find a space that closes deals for you.`,
  (name: string) =>
    `Welcome back, ${name}. One step closer to your dream office.`,
  (name: string) =>
    `Welcome back, ${name}. Let's land the space you've been looking for.`,
  (name: string) => `${name}, you're back—let's make your next lease a flex.`,
  (name: string) =>
    `Back again, ${name}. Let's find a space that closes deals for you.`,
  (name: string) =>
    `Good to see you, ${name}. Your next office should feel like a promotion.`,
  (name: string) => `Back in action, ${name}—let's go find your next HQ.`,
  (name: string) => `${name}, let's find a space worth bragging about.`,
  (name: string) => `You're in, ${name}—let's lock in your ideal office.`,
  (name: string) =>
    `Back at it, ${name}. Let's turn your next lease into a power move.`,
  (name: string) =>
    `${name}, your dream office isn't going to find itself—let's go.`,
  (name: string) => `Let's make moves, ${name}—your next office is waiting.`,
  (name: string) => `${name}, time to upgrade your office situation.`,
  (name: string) => `Back in the hunt, ${name}—let's find a winner.`,
];

const SESSION_FLAG = "kollmann:greeted-this-tab";

export default function GreetingOverlay() {
  useDebugRenderLoop("GreetingOverlay", "root");
  const { data: session, status } = useSession();
  const [visible, setVisible] = useState(false);
  const [greeting, setGreeting] = useState("");
  const decided = useRef(false);

  useEffect(() => {
    if (decided.current) return;
    if (status !== "authenticated") return;

    decided.current = true;

    if (sessionStorage.getItem(SESSION_FLAG)) return;

    const rawFirst = session?.user?.name?.split(" ")[0] ?? "there";
    const firstName = rawFirst.charAt(0).toUpperCase() + rawFirst.slice(1);
    const random = GREETINGS[Math.floor(Math.random() * GREETINGS.length)]!;

    setGreeting(random(firstName));
    setVisible(true);
    sessionStorage.setItem(SESSION_FLAG, "1");
  }, [status, session]);

  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => setVisible(false), 2500);
    return () => clearTimeout(timer);
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          onClick={() => setVisible(false)}
          className="fixed inset-0 z-[999] flex cursor-pointer items-center justify-center bg-black"
        >
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="max-w-3xl px-8 text-center font-lustria text-3xl font-light tracking-wide text-white sm:text-5xl"
          >
            {greeting}
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
